<template>
    <v-container fluid>
        <!-- KPI -->
        <v-row class="mb-2">
            <v-col cols="6" md="3">
                <v-card variant="outlined" class="rounded-lg" height="100%">
                    <v-card-text>
                        <div class="d-flex align-center justify-space-between">
                            <div>
                                <div class="text-caption text-medium-emphasis">Dispositivi</div>
                                <div class="text-h4 font-weight-bold">{{ kpi.totaleDispositivi ?? '—' }}</div>
                                <div class="text-caption text-medium-emphasis">Totali censiti</div>
                            </div>
                            <v-avatar color="blue-lighten-4" size="44">
                                <v-icon color="primary">mdi-shield-check</v-icon>
                            </v-avatar>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="6" md="3">
                <v-card variant="outlined" class="rounded-lg kpi-cliccabile" height="100%" @click="apriAttenzione">
                    <v-card-text>
                        <div class="d-flex align-center justify-space-between">
                            <div>
                                <div class="text-caption text-medium-emphasis">Contenuti scaduti</div>
                                <div class="text-h4 font-weight-bold">{{ kpi.contenutiScaduti ?? '—' }}</div>
                                <div class="text-caption text-medium-emphasis">Da sostituire</div>
                            </div>
                            <v-avatar color="red-lighten-4" size="44">
                                <v-icon color="error">mdi-calendar-alert</v-icon>
                            </v-avatar>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="6" md="3">
                <v-card variant="outlined" class="rounded-lg kpi-cliccabile" height="100%" @click="apriAttenzione">
                    <v-card-text>
                        <div class="d-flex align-center justify-space-between">
                            <div>
                                <div class="text-caption text-medium-emphasis">In scadenza</div>
                                <div class="text-h4 font-weight-bold">{{ kpi.contenutiInScadenza ?? '—' }}</div>
                                <div class="text-caption text-medium-emphasis">Entro 14 giorni</div>
                            </div>
                            <v-avatar color="orange-lighten-4" size="44">
                                <v-icon color="warning">mdi-clock-alert-outline</v-icon>
                            </v-avatar>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Filtri -->
        <v-row class="align-center mb-2">
            <v-col cols="12" sm="6" md="4">
                <v-text-field v-model="ricerca" label="Cerca dispositivo" prepend-inner-icon="mdi-magnify"
                    density="compact" variant="outlined" hide-details clearable />
            </v-col>
            <v-col cols="12" sm="6" md="4">
                <v-select v-model="tipoId" :items="tipi" item-title="nome" item-value="id"
                    label="Tutti i tipi" density="compact" variant="outlined" hide-details clearable
                    @update:modelValue="carica" />
            </v-col>
        </v-row>

        <v-row class="align-center mb-2">
            <v-col cols="12" sm="auto" class="mr-auto">
                <span class="text-body-2 text-medium-emphasis">
                    {{ dispositivi.length }} dispositiv{{ dispositivi.length === 1 ? 'o trovato' : 'i trovati' }}
                </span>
            </v-col>
            <v-col cols="12" sm="auto" v-if="isAdmin">
                <v-btn variant="outlined" prepend-icon="mdi-cog-outline" to="/tipi-sicurezza">
                    Tipi dispositivo
                </v-btn>
            </v-col>
            <v-col cols="12" sm="auto" v-if="puoScrivere">
                <v-btn color="primary" prepend-icon="mdi-plus" @click="apriDialog(null)">
                    Nuovo dispositivo
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col v-for="d in dispositivi" :key="d.id" cols="12" sm="6" md="4" lg="3">
                <v-card variant="outlined" class="rounded-lg dispositivo-card" @click="vaiDettaglio(d.id)">
                    <v-card-text>
                        <div class="d-flex align-center justify-space-between">
                            <div class="font-weight-bold text-body-1">{{ d.nome }}</div>
                            <v-chip size="small" :color="d.colore || 'grey'" variant="flat">
                                {{ d.tipoDispositivoSicurezzaNome }}
                            </v-chip>
                        </div>
                        <div class="text-caption text-medium-emphasis mt-1">
                            <span v-if="d.repartoNome">{{ d.repartoNome }}</span>
                            <span v-else-if="d.mezzoNome">{{ d.mezzoNome }}</span>
                            <span v-else-if="d.sedeNome">{{ d.sedeNome }}</span>
                            <span v-else>—</span>
                        </div>
                        <div class="text-caption mt-2">
                            <div><span class="text-medium-emphasis">Contenuti attivi:</span> {{ d.totaleContenuti }}</div>
                            <div><span class="text-medium-emphasis">Prossima scadenza:</span>
                                {{ formatData(d.prossimaScadenza) }}</div>
                        </div>
                        <v-chip v-if="d.contenutiScaduti > 0" size="small" color="error" variant="flat" class="mt-2">
                            {{ d.contenutiScaduti }} scadut{{ d.contenutiScaduti === 1 ? 'o' : 'i' }}
                        </v-chip>
                    </v-card-text>
                    <v-card-actions v-if="puoScrivere">
                        <v-spacer />
                        <v-btn icon variant="text" size="small" title="Duplica" @click.stop="duplica(d)">
                            <v-icon>mdi-content-copy</v-icon>
                        </v-btn>
                        <v-btn icon variant="text" size="small" @click.stop="apriDialog(d)">
                            <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn icon variant="text" size="small" color="error" @click.stop="chiediEliminazione(d)">
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>

            <v-col cols="12" v-if="dispositivi.length === 0">
                <div class="text-center text-medium-emphasis pa-8">Nessun dispositivo trovato con questi filtri.</div>
            </v-col>
        </v-row>

        <!-- Dialog creazione/modifica -->
        <dialog-dispositivo-sicurezza v-model="dialog" :dispositivo="dispositivoSelezionato" @salvato="onSalvato" />

        <!-- Dialog "in attenzione": scaduti + in scadenza entro 14gg, su tutti i dispositivi -->
        <v-dialog v-model="dialogAttenzione" max-width="560">
            <v-card>
                <v-card-title>Contenuti scaduti o in scadenza</v-card-title>
                <v-card-text>
                    <div v-if="contenutiAttenzione.length === 0" class="text-medium-emphasis pa-4 text-center">
                        Nessun contenuto scaduto o in scadenza nei prossimi 14 giorni.
                    </div>
                    <v-list v-else density="compact">
                        <v-list-item v-for="c in contenutiAttenzione" :key="c.id"
                            :title="`${c.dispositivoSicurezzaNome} — ${c.nome}`"
                            :subtitle="descrizioneAttenzione(c)" class="attenzione-riga"
                            @click="vaiDettaglio(c.dispositivoSicurezzaId); dialogAttenzione = false">
                            <template #prepend>
                                <v-chip size="small" :color="c.effettivamenteScaduto ? 'error' : 'warning'"
                                    variant="flat">
                                    {{ c.effettivamenteScaduto ? 'Scaduto' : 'In scadenza' }}
                                </v-chip>
                            </template>
                        </v-list-item>
                    </v-list>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="dialogAttenzione = false">Chiudi</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog conferma eliminazione -->
        <v-dialog v-model="dialogElimina" max-width="420">
            <v-card>
                <v-card-title>Confermi l'eliminazione?</v-card-title>
                <v-card-text>
                    Il dispositivo "{{ dispositivoDaEliminare?.nome }}" verrà rimosso (spostato nello storico).
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="dialogElimina = false">Annulla</v-btn>
                    <v-btn color="error" @click="confermaEliminazione">Elimina</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog di conflitto: il dispositivo ha contenuti collegati -->
        <v-dialog v-model="dialogConflitto" max-width="480">
            <v-card>
                <v-card-title>Il dispositivo ha contenuti collegati</v-card-title>
                <v-card-text>
                    <div class="mb-2">{{ conflittoMessaggio }}</div>
                    <v-list density="compact">
                        <v-list-item v-for="el in conflittoElementi" :key="el.id"
                            :title="formatData(el.data)" :subtitle="el.stato" />
                    </v-list>
                    <div class="mt-2 text-error">Procedendo verranno eliminati anche tutti questi contenuti.</div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="dialogConflitto = false">Annulla</v-btn>
                    <v-btn color="error" @click="eliminaComunque">Elimina comunque</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import DialogDispositivoSicurezza from "@/components/sicurezza/DialogDispositivoSicurezza.vue";

export default {
    name: "SicurezzaPage",

    components: {
        DialogDispositivoSicurezza,
    },

    data() {
        return {
            ricerca: "",
            tipoId: null,
            dialog: false,
            dispositivoSelezionato: null,
            dialogElimina: false,
            dispositivoDaEliminare: null,
            dialogConflitto: false,
            conflittoMessaggio: "",
            conflittoElementi: [],
            debounce: null,
            dialogAttenzione: false,
        };
    },

    computed: {
        dispositivi() {
            return this.$store.getters["dispositiviSicurezza/lista"];
        },
        kpi() {
            return this.$store.getters["dispositiviSicurezza/kpi"] || {};
        },
        contenutiAttenzione() {
            return this.$store.getters["contenutiSicurezza/lista"];
        },
        tipi() {
            return this.$store.getters["tipiDispositivoSicurezza/lista"];
        },
        ruolo() {
            return this.$store.getters["user/getRole"];
        },
        puoScrivere() {
            return this.ruolo === "SuperAdmin" || this.ruolo === "Admin" || this.ruolo === "Operatore";
        },
        isAdmin() {
            return this.ruolo === "SuperAdmin" || this.ruolo === "Admin";
        },
    },

    watch: {
        ricerca() {
            clearTimeout(this.debounce);
            this.debounce = setTimeout(() => this.carica(), 300);
        },
    },

    mounted() {
        this.$store.dispatch("tipiDispositivoSicurezza/carica");
        this.$store.dispatch("dispositiviSicurezza/caricaKpi");
        this.carica();
    },

    methods: {
        async carica() {
            try {
                await this.$store.dispatch("dispositiviSicurezza/carica", {
                    search: this.ricerca || undefined,
                    tipoId: this.tipoId || undefined,
                });
            } catch (error) {
                console.log(error);
            }
        },

        vaiDettaglio(id) {
            this.$router.push({ path: `/sicurezza/${id}` });
        },

        apriDialog(item) {
            this.dispositivoSelezionato = item;
            this.dialog = true;
        },

        onSalvato() {
            this.carica();
            this.$store.dispatch("dispositiviSicurezza/caricaKpi");
        },

        async duplica(item) {
            try {
                await this.$store.dispatch("dispositiviSicurezza/duplica", item.id);
                this.carica();
                this.$store.dispatch("dispositiviSicurezza/caricaKpi");
            } catch (error) {
                console.log(error);
            }
        },

        chiediEliminazione(item) {
            this.dispositivoDaEliminare = item;
            this.dialogElimina = true;
        },

        async confermaEliminazione() {
            try {
                await this.$store.dispatch("dispositiviSicurezza/elimina", { id: this.dispositivoDaEliminare.id });
                this.carica();
                this.$store.dispatch("dispositiviSicurezza/caricaKpi");
                this.dialogElimina = false;
            } catch (error) {
                if (error.response && error.response.status === 409) {
                    this.conflittoMessaggio = error.response.data.message;
                    this.conflittoElementi = error.response.data.elementi || [];
                    this.dialogElimina = false;
                    this.dialogConflitto = true;
                } else {
                    console.log(error);
                    this.dialogElimina = false;
                }
            }
        },

        async eliminaComunque() {
            try {
                await this.$store.dispatch("dispositiviSicurezza/elimina", {
                    id: this.dispositivoDaEliminare.id,
                    conferma: true,
                });
                this.carica();
                this.$store.dispatch("dispositiviSicurezza/caricaKpi");
            } catch (error) {
                console.log(error);
            }
            this.dialogConflitto = false;
        },

        formatData(data) {
            return data ? new Date(data).toLocaleDateString("it-IT") : "—";
        },

        async apriAttenzione() {
            this.dialogAttenzione = true;
            try {
                await this.$store.dispatch("contenutiSicurezza/carica", { entroGiorni: 14 });
            } catch (error) {
                console.log(error);
            }
        },

        descrizioneAttenzione(c) {
            return c.dataScadenza ? `scade il ${this.formatData(c.dataScadenza)}` : "";
        },
    },
};
</script>

<style scoped>
.dispositivo-card {
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s;
}

.dispositivo-card:hover {
    border-color: rgb(var(--v-theme-primary));
}

.kpi-cliccabile {
    cursor: pointer;
    transition: border-color 0.15s;
}

.kpi-cliccabile:hover {
    border-color: rgb(var(--v-theme-primary));
}

.attenzione-riga {
    cursor: pointer;
}
</style>
